'use client';

import { percentFormatter } from '@/utils/currency';
import { useState } from 'react';
import useProductCollection from '@/useProductionCollection';
import { notFound } from 'next/navigation';
import Image from 'next/image';
import { Badge } from '@/components/ui/badge';
import { productImageSrc, productThumbnails } from '@/lib/product-image';

export default function ImageSelector({
  product,
}: {
  product: {
    title: string;
    image: string;
    discount: number;
    thumbnails: [];
  };
}) {
  if (!product.title) {
    notFound();
  }

  const thumbnails = productThumbnails(product.thumbnails);
  const [selectedImage, setSelectedImage] = useState(productImageSrc(product.image));
  const handleImageClick = (image) => () => setSelectedImage(image);
  const { isProductAdded, handleCollectionClick } =
    useProductCollection(product);

  return (
    <div className="pb-4">
      <div className="flex justify-center mb-2 relative">
        {product.discount > 0.01 && (
          <Badge className="absolute top-2 left-2 z-10 font-bold text-sm">
            - {percentFormatter.format(product.discount)}
          </Badge>
        )}
        <button
          onClick={handleCollectionClick}
          className="absolute top-0 right-0 z-10 p-2 cursor-pointer bg-transparent border-none"
          aria-label="Add to favorites"
        >
          {isProductAdded ? (
            <svg className="w-10 h-10" viewBox="0 0 24 24" style={{ fill: 'hsl(var(--primary))' }}>
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
            </svg>
          ) : (
            <svg className="w-10 h-10" viewBox="0 0 24 24" style={{ fill: 'none', stroke: 'currentColor', strokeWidth: 2 }}>
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
            </svg>
          )}
        </button>
        {selectedImage && (
          <Image
            alt={product.title}
            width={300}
            height={300}
            src={selectedImage}
            className="w-[60%] md:w-3/4 h-auto object-contain aspect-square"
          />
        )}
      </div>
      {thumbnails.length > 1 && (
        <div
          className="flex flex-row overflow-x-auto gap-2 px-2"
          style={{ scrollbarWidth: 'thin' }}
        >
          {thumbnails.map((url, ind) => (
            <div
              key={ind}
              className="flex items-center justify-center w-16 h-16 min-w-[64px] shrink-0 bg-white border rounded-[10px] cursor-pointer"
              style={{
                borderColor: selectedImage === url ? 'hsl(var(--primary))' : 'hsl(var(--border))',
              }}
              onClick={handleImageClick(url)}
            >
              {url && product.title && (
                <Image
                  src={url}
                  alt={product.title}
                  width={56}
                  height={56}
                  style={{
                    objectFit: 'contain',
                  }}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
